<template>
  <div v-if="visible" class="ca-attention" :class="{ 'is-collapsed': collapsed }" role="region" :aria-label="$t('ca_attention_title')">
    <button
      type="button"
      class="ca-attention__head"
      :aria-expanded="!collapsed"
      :title="collapsed ? $t('ca_attention_show') : $t('ca_attention_hide')"
      @click="toggleCollapsed"
    >
      <span
        class="ca-attention__chevron"
        :class="{ 'is-collapsed': collapsed }"
        aria-hidden="true"
      >▾</span>
      <span class="ca-attention__title">{{ $t('ca_attention_title') }}</span>
      <span v-if="items.length" class="ca-attention__count">{{ items.length }}</span>
    </button>

    <template v-if="!collapsed">
      <p v-if="!items.length" class="ca-attention__clear">{{ $t('ca_attention_empty') }}</p>

      <div v-else class="ca-attention__list" :dir="isRTL ? 'rtl' : 'ltr'">
        <button
          v-for="item in items"
          :key="item.key"
          type="button"
          class="ca-attention__chip"
          :class="`is-${item.type}`"
          :title="chipLabel(item)"
          @click="$emit('focus-cleaner', item.employeeId)"
        >
          <span class="ca-attention__chip-icon" aria-hidden="true">{{ attentionTypeIcon(item.type) }}</span>
          <span class="ca-attention__chip-text">{{ chipLabel(item) }}</span>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { buildAttentionItems, attentionItemLabel, attentionTypeIcon } from './cleanerActivityAttention'

const COLLAPSE_STORAGE_KEY = 'ca_attention_collapsed'

export default {
  props: {
    cleaners: { type: Array, default: () => [] },
    slaMinutes: { type: [Number, String], default: null },
    shiftStart: { type: Date, default: null },
    idleThresholdMinutes: { type: Number, default: null },
    nowTick: { type: Number, default: () => Date.now() },
    hidden: { type: Boolean, default: false },
  },
  data() {
    return {
      collapsed: this.readCollapsed(),
    }
  },
  computed: {
    ...mapGetters(['isRTL']),
    visible() {
      return !this.hidden && this.cleaners.length > 0
    },
    items() {
      void this.nowTick
      return buildAttentionItems(this.cleaners, {
        slaMinutes: this.slaMinutes,
        shiftStart: this.shiftStart,
        nowMs: this.nowTick,
        idleThresholdMinutes: this.idleThresholdMinutes,
      })
    },
  },
  methods: {
    attentionTypeIcon,
    chipLabel(item) {
      return attentionItemLabel(item, this.$t.bind(this))
    },
    readCollapsed() {
      try {
        return localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1'
      } catch (err) {
        return false
      }
    },
    toggleCollapsed() {
      this.collapsed = !this.collapsed
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, this.collapsed ? '1' : '0')
      } catch (err) {
        // Ignore storage failures (private mode, quota); collapse still works in-session.
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.ca-attention {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin-bottom: 8px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    text-align: start;

    &:focus-visible {
      outline: 2px solid rgba(47, 128, 235, 0.45);
      outline-offset: 2px;
      border-radius: 4px;
    }
  }

  &.is-collapsed &__head {
    margin-bottom: 0;
  }

  &__chevron {
    font-size: 0.7rem;
    line-height: 1;
    color: #9aa5b1;
    transition: transform 0.15s ease;

    &.is-collapsed {
      transform: rotate(-90deg);
    }
  }

  &__title {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #9aa5b1;
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: #fdecea;
    color: #c0392b;
    font-size: 0.72rem;
    font-weight: 700;
  }

  &__clear {
    margin: 0;
    font-size: 0.84rem;
    color: #5f9b46;
    font-weight: 500;
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 5px 10px;
    border: 1px solid transparent;
    border-radius: 999px;
    background: #f8f9fb;
    color: #2c3e50;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    &.is-sla_breach {
      background: #fdecea;
      border-color: #f5c6c2;
      color: #922b21;
    }

    &.is-open_while_out {
      background: #fff4e5;
      border-color: #f8d7a5;
      color: #9a5b0a;
    }

    &.is-idle {
      background: #eef3f8;
      border-color: #c9d6e3;
      color: #3d5a73;
    }

    &.is-late {
      background: #f3f0ff;
      border-color: #d6cbff;
      color: #5b4b8a;
    }
  }

  &__chip-icon {
    flex-shrink: 0;
    font-size: 0.82rem;
    line-height: 1;
  }

  &__chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

@media (max-width: 767px) {
  .ca-attention {
    padding: 8px 10px;

    &__head {
      margin-bottom: 6px;
    }

    &__list {
      gap: 6px;
    }

    &__chip {
      flex: 1 1 100%;
      max-width: 100%;
      justify-content: flex-start;
      padding: 7px 10px;
      font-size: 0.76rem;
    }

    &__chip-text {
      white-space: normal;
    }
  }
}
</style>
